/**
 * PitchBlackDock — a floating frosted "island" nav dock for the Pitch
 * Black event skin. Pill-shaped glass bar, centered at the bottom, with
 * neon-magenta active states. Renders only while the Pitch Black theme
 * is on (parent passes an empty `items` array to hide it). Purely a
 * shortcut layer — it drives the app's existing navigation handlers.
 *
 * Auto-hide (user ask 2026-09-11 "the bottom menu is in the way of other
 * screens"): the dock floats above everything, so it was covering the
 * action buttons (Handoff / Mark Complete) at the bottom of open sheets
 * and dialogs. We now hide it whenever any Radix Sheet/Dialog is open —
 * detected via the body scroll-lock they set + an open [role="dialog"].
 */
import { useEffect, useState } from "react";

export default function PitchBlackDock({ items = [] }) {
  const [overlayOpen, setOverlayOpen] = useState(false);

  useEffect(() => {
    const check = () => {
      const modal = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
      );
      // react-remove-scroll (used by every shadcn Dialog/Sheet) stamps
      // this on <body> while a modal/sheet is mounted.
      const scrollLocked = document.body.hasAttribute("data-scroll-locked");
      setOverlayOpen(Boolean(modal) || scrollLocked);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-scroll-locked", "style"],
      childList: true,
    });
    return () => obs.disconnect();
  }, []);

  if (!items.length || overlayOpen) return null;
  return (
    <nav
      className="fixed left-1/2 -translate-x-1/2 z-[60] pointer-events-auto"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)" }}
      data-testid="pitch-black-dock"
      aria-label="Quick navigation"
    >
      <div
        className="flex items-center gap-1 px-2 py-2 rounded-[26px]"
        style={{
          background: "linear-gradient(135deg, rgba(20,16,26,0.72) 0%, rgba(6,6,10,0.82) 100%)",
          backdropFilter: "blur(20px) saturate(170%)",
          WebkitBackdropFilter: "blur(20px) saturate(170%)",
          border: "1px solid rgba(236,0,140,0.4)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.7), 0 0 24px rgba(236,0,140,0.22), inset 0 1px 1px rgba(255,255,255,0.12)",
        }}
      >
        {items.map(({ key, label, Icon, active, onClick }) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            data-testid={`pb-dock-${key}`}
            aria-current={active ? "page" : undefined}
            className="relative flex flex-col items-center justify-center rounded-[20px] px-4 py-1.5 min-w-[62px] transition-all active:scale-95"
            style={
              active
                ? {
                    background: "linear-gradient(135deg, #EC008C 0%, #A855F7 100%)",
                    boxShadow: "0 0 18px rgba(236,0,140,0.6)",
                    color: "#FFFFFF",
                  }
                : { background: "transparent", color: "rgba(245,245,245,0.72)" }
            }
          >
            <Icon weight={active ? "fill" : "regular"} className="w-5 h-5" />
            <span
              className="mt-0.5 text-[10px] font-bold tracking-wide"
              style={{ textShadow: active ? "0 0 8px rgba(236,0,140,0.5)" : "none" }}
            >
              {label}
            </span>
            {active && (
              <span
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                style={{ background: "#A3E635", boxShadow: "0 0 8px #A3E635" }}
                aria-hidden
              />
            )}
          </button>
        ))}
      </div>
    </nav>
  );
}
